import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaEnvelope, FaLock, FaArrowLeft, FaQuestionCircle, FaCheckCircle } from 'react-icons/fa';
import axios from 'axios';

const ForgotPassword = () => {
  const [step, setStep] = useState(1); // 1: email, 2: answer, 3: success
  const [email, setEmail] = useState('');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const API_BASE = process.env.REACT_APP_API_URL || '/api';

  // Step 1: Find User & Get Question
  const handleFindUser = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const res = await axios.get(`${API_BASE}/auth/security-question/${email}`);
      setQuestion(res.data.question);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.message || 'User not found or error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Verify Answer & Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Password strength check
    if (newPassword.length < 8 || !/[A-Z]/.test(newPassword) || !/[0-9]/.test(newPassword)) {
      setError('Password must be 8+ chars and include one uppercase letter and one number.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await axios.post(`${API_BASE}/auth/reset-password-security`, {
        email,
        answer,
        newPassword
      });
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.message || 'Incorrect answer or server error');
    } finally {
      setIsLoading(false);
    }
  };

  const background = (
    <div className="absolute inset-0 overflow-hidden">
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-gold/5 blur-[120px] rounded-full"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-gold/5 blur-[120px] rounded-full"></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-maroon-dark flex items-center justify-center p-4 pt-24 relative overflow-hidden">
      {background}

      <div className="w-full max-w-md relative z-10">
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-10 border border-gold/10 shadow-2xl">
          
          {step === 1 && (
            <>
              <div className="text-center mb-10">
                <h2 className="text-4xl font-serif font-bold text-gold-light mb-2">Forgot Password</h2>
                <p className="text-gold-light/50 font-sans font-medium">Verify your email to reset using your security question</p>
              </div>

              <form onSubmit={handleFindUser} className="space-y-6">
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gold-light/40 group-focus-within:text-gold transition-colors">
                    <FaEnvelope className="h-5 w-5" />
                  </div>
                  <input
                    type="email"
                    placeholder="Enter your registered email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="block w-full pl-12 pr-4 py-4 bg-white/5 border border-gold/10 rounded-2xl focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold/40 transition-all text-white placeholder-gold-light/30 font-sans"
                    required
                  />
                </div>

                {error && (
                  <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-2xl text-sm font-medium font-sans">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full h-14 btn-gold !rounded-2xl flex items-center justify-center space-x-3 text-base"
                >
                  {isLoading ? (
                    <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    <span>Find My Account</span>
                  )}
                </button>
              </form>
            </>
          )}

          {step === 2 && (
            <>
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FaQuestionCircle className="text-gold text-4xl" />
                </div>
                <h2 className="text-3xl font-serif font-bold text-gold-light mb-2">Security Question</h2>
                <p className="text-gold italic font-sans font-medium">"{question}"</p>
              </div>

              <form onSubmit={handleResetPassword} className="space-y-6">
                <div className="space-y-4">
                  <input
                    type="text"
                    placeholder="Your Answer"
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    className="block w-full px-4 py-3.5 bg-white/5 border border-gold/10 rounded-2xl focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold/40 transition-all text-white placeholder-gold-light/30"
                    required
                  />
                  
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gold-light/40 group-focus-within:text-gold transition-colors">
                      <FaLock className="h-5 w-5" />
                    </div>
                    <input
                      type="password"
                      placeholder="New Password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="block w-full pl-12 pr-4 py-3.5 bg-white/5 border border-gold/10 rounded-2xl focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold/40 transition-all text-white placeholder-gold-light/30"
                      required
                    />
                  </div>

                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gold-light/40 group-focus-within:text-gold transition-colors">
                      <FaLock className="h-5 w-5" />
                    </div>
                    <input
                      type="password"
                      placeholder="Confirm New Password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="block w-full pl-12 pr-4 py-3.5 bg-white/5 border border-gold/10 rounded-2xl focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold/40 transition-all text-white placeholder-gold-light/30"
                      required
                    />
                  </div>
                </div>

                {error && (
                  <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-2xl text-sm font-medium font-sans">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full h-14 btn-gold !rounded-2xl flex items-center justify-center space-x-3 text-base"
                >
                  {isLoading ? (
                    <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    <span>Reset Password</span>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="w-full text-gold-light/40 hover:text-gold transition-colors text-sm"
                >
                  ← Use different email
                </button>
              </form>
            </>
          )}

          {step === 3 && (
            <div className="text-center p-4">
              <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <FaCheckCircle className="text-green-500 text-4xl" />
              </div>
              <h2 className="text-3xl font-serif font-bold text-gold-light mb-4">Password Reset!</h2>
              <p className="text-gold-light/60 font-sans mb-8">
                Your password has been successfully updated. You can now log in with your new credentials.
              </p>
              <button
                onClick={() => navigate('/login')}
                className="w-full h-14 btn-gold !rounded-2xl flex items-center justify-center text-base"
              >
                Go to Login
              </button>
            </div>
          )}

          {step !== 3 && (
            <Link
              to="/login"
              className="w-full mt-6 flex items-center justify-center space-x-2 py-4 bg-transparent text-gold-light/70 font-medium hover:text-gold transition-all font-sans"
            >
              <FaArrowLeft />
              <span>Back to Login</span>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
